import { Subathon } from '../api/payment/subathon.api';
import { parseServerDate } from './server-date';

// Hàm thuần cho khối cài đặt + overlay OBS "đếm ngược subathon" — định dạng thời gian và tính
// số giây còn lại tại một thời điểm bất kỳ. Tách khỏi component để test không phải dựng
// React/SignalR.

const pad2 = (n: number) => `${n}`.padStart(2, '0');

/** Số giây ⇒ "h:mm:ss" (khi có giờ) hoặc "m:ss" (dưới 1 giờ); 0 hoặc âm ⇒ "0:00". */
export const formatHms = (seconds: number): string => {
  const total = Math.max(0, Math.floor(seconds || 0));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}:${pad2(m)}:${pad2(s)}`;
  return `${m}:${pad2(s)}`;
};

/** Nhãn "vừa cộng thêm bao nhiêu giờ" cho hiệu ứng overlay: "+2:30", "+1:00:00", "+0:00". */
export const formatAdd = (seconds: number): string => `+${formatHms(seconds)}`;

/**
 * Độ lệch đồng hồ máy người xem so với máy chủ (ms), lấy tại thời điểm tải snapshot — dùng làm
 * `skewMs` cho `remainingAt` khi tick mỗi giây về sau, để đếm ngược không lệch khi máy người xem
 * chỉnh sai giờ.
 */
export const clockSkewMs = (serverNowIso: string, clientNowMs: number = Date.now()): number => {
  const serverNowMs = parseServerDate(serverNowIso)?.getTime();
  return serverNowMs == null ? 0 : serverNowMs - clientNowMs;
};

/**
 * Số giây còn lại tại thời điểm `nowMs` (đồng hồ máy người xem, cộng thêm `skewMs` nếu có):
 * đang chạy (`status === 'Running'`) và không tạm dừng thì trừ đi thời gian đã trôi kể từ
 * `serverNow` của snapshot; tạm dừng, đã kết thúc hoặc không có subathon thì giữ nguyên
 * `remainingSeconds` đã tính sẵn từ máy chủ. Không bao giờ trả về số âm.
 */
export const remainingAt = (
  s: Pick<Subathon, 'remainingSeconds' | 'serverNow' | 'status' | 'isPaused'>,
  nowMs: number,
  skewMs: number = 0
): number => {
  const remaining = Math.max(0, s.remainingSeconds || 0);
  if (s.status !== 'Running' || s.isPaused) return remaining;

  const serverNowMs = parseServerDate(s.serverNow)?.getTime();
  if (serverNowMs == null) return remaining;

  const elapsedSeconds = Math.max(0, Math.floor((nowMs + skewMs - serverNowMs) / 1000));
  return Math.max(0, remaining - elapsedSeconds);
};

/** Cửa sổ hiện "Hết giờ!" trên overlay sau khi subathon kết thúc, trước khi ẩn hẳn. */
export const ENDED_VISIBLE_MS = 10 * 60 * 1000;

/**
 * True khi subathon đã kết thúc (`status === 'Ended'`) và còn trong cửa sổ `ENDED_VISIBLE_MS`
 * kể từ `endedAt` — overlay dùng để quyết định hiện "Hết giờ!" hay ẩn hẳn. Dùng đồng hồ ĐÃ BÙ
 * `skewMs` (giống `remainingAt`) thay vì `Date.now()` thô, để không lệch cửa sổ 10 phút khi máy
 * người xem chỉnh sai giờ.
 */
export const isEndedVisible = (
  s: Pick<Subathon, 'status' | 'endedAt'>,
  nowMs: number,
  skewMs: number = 0
): boolean => {
  if (s.status !== 'Ended' || !s.endedAt) return false;
  const endedAtMs = parseServerDate(s.endedAt)?.getTime();
  if (endedAtMs == null) return false;
  return nowMs + skewMs - endedAtMs <= ENDED_VISIBLE_MS;
};
